import { InvoicePaymentStatus } from '../models/invoice-payment-status';
import dateFormat from 'dateformat';
import { EditInvoice, Invoice, } from '../models/table-models';
import { invoicePaymentValue, invoiceStateValue } from './Enums/InvoiceFields';
import { Position } from '../context/BranchesContext';
import { DocumentView } from "../models/dashboard-models";


/**
 * Formats a Date as a local Sofia (Europe/Sofia) datetime string without UTC conversion.
 * e.g. "2026-04-07T12:30:00"
 * Use instead of date.toISOString() when the backend stores LocalDateTime (no timezone).
 */
export const toSofiaLocalISOString = (date: Date): string => {
    const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/Sofia',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
    }).formatToParts(date);
    const p = Object.fromEntries(parts.map(({ type, value }) => [type, value]));
    return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}:${p.second}`;
};

/**
 * Първият ден на текущия месец, във формата, който DateFilterComponent очаква (yyyy-mm-dd).
 *
 * Ползва се като начална стойност на филтъра "от дата" на екрана с плащанията - иначе той се отваря върху
 * цялата история и първата страница е от най-старите записи.
 */
export const firstDayOfCurrentMonth = (): string => {
    const now = new Date();
    return dateFormat(new Date(now.getFullYear(), now.getMonth(), 1), 'yyyy-mm-dd');
};

export const transformDateToString = (value: Date | string, mask?: string) => {
    return dateFormat(value, mask ? mask : 'dd/mm/yyyy');
};

export const transformPriceToString = (value: number) => {
    let formattedValue = (value / 100).toLocaleString('en-EN', { maximumFractionDigits: 2 });
    // Remove commas before converting
    return parseFloat(formattedValue.replace(/,/g, ''));
};

export const transformInvoiceToInput = (fieldName: string, value: string | number | undefined | DocumentView | Date | boolean) => {
    if (fieldName === 'fromDate' || fieldName === 'duePaymentDate' || fieldName === 'creationDate')
        value = transformDateToString(value as Date);
    else if (fieldName === 'fee' || fieldName === 'totalFee' || fieldName === 'total')
        value = transformPriceToString(value as number);
    return value;
};

export const getViewEditInvoice = (item?: Invoice): EditInvoice => {
    return {
        id: item?.id,
        state: invoiceStateValue(item?.state),
        paid: invoicePaymentValue(item?.paymentMethod),
        isPaid: item?.paid ? InvoicePaymentStatus.Paid : InvoicePaymentStatus.Unpaid,
        taskType: 'MAINTENANCE',
        total: Number(transformPriceToString(item ? item.total : 1)),
        creationDate: item?.creationDate?.toLocaleString() || new Date().toLocaleString(),
        fromDate: item?.fromDate?.toLocaleString() || new Date().toLocaleString(),
        duePaymentDate: item?.duePaymentDate.toLocaleString() || new Date().toLocaleString(),
        comment: item?.comment || "",
    };
};

export const sortAlphabetically = (a: string, b: string) => {
    const nameA = a.toUpperCase(); // ignore upper and lowercase
    const nameB = b.toUpperCase(); // ignore upper and lowercase
    if (nameA < nameB) {
        return -1;
    }
    if (nameA > nameB) {
        return 1;
    }

    return 0;
};

export const parseTimeToDate = (timeString: string, format: string) => {
    let timeSeparator = timeString.indexOf(':');
    let hours = timeString.slice(timeSeparator - 2, timeSeparator);
    let minutes = timeString.slice(timeSeparator + 1, timeSeparator + 3);

    let now = new Date();
    if (format === 'HH:mm') {
        now.setHours(+hours, +minutes);
        return now;
    } else return new Date();
};

export const parseDateToTime = (dateString: string) => {
    let timeSeparator = dateString.indexOf(':');
    let hours = dateString.slice(timeSeparator - 2, timeSeparator);
    let minutes = dateString.slice(timeSeparator + 1, timeSeparator + 3);

    return `${hours}:${minutes}`;
};

export const getInvoiceColor = (invoice: Invoice, invoiceDays: number): string => {
    const oneDay = 1000 * 60 * 60 * 24;
    const date = Date.now();
    const daysUntilPayment = (Date.parse(`${invoice.duePaymentDate}`) - date) / oneDay;
    const isPaymentSoon = daysUntilPayment < invoiceDays;
    return !invoice.paid && isPaymentSoon ? 'orange' : invoice.paid ? 'green' : 'red';
};

export const positionToLocation = (newMarker: Position) => ({
    latitude: String(newMarker.lat),
    longitude: String(newMarker.lng),
    address: '',
    city: '',
});

export const calculateConsumption = (
    oilConsumptionPerHour: number,
    oilCapacity: number,
    waitTime: number,
    sprayTime: number
): { secondsWait: number; secondsSpray: number; consumptionPerSecond: number; predictedWorkTime: number } => {
    const cycles = 3600 / (+sprayTime + +waitTime);
    const secondsWait = cycles * waitTime;
    const secondsSpray = cycles * sprayTime;
    // const consumptionPerSecond = oilConsumptionPerHour / (3600 - secondsWait);
    const consumptionPerSecond = (secondsSpray / 3600) * oilConsumptionPerHour;
    const predictedWorkTime = oilCapacity / oilConsumptionPerHour;
    return { secondsWait, secondsSpray, consumptionPerSecond, predictedWorkTime };
};



interface HasCountAndCollection<T> {
    count: number;
    cost?: number; // Optional, as not all item types may have cost
    collectionOfItems?: T[];
}

// Generic function Format Table Data for - Warehouse, Inventory and Referce tables
export const formatQuantitiesByPassport = <T extends HasCountAndCollection<T>>(
    items: T[],
    keySelector: (item: T) => string | number,
    aggregate: (existingItem: T, newItem: T) => void,
    transform?: (item: T) => void
): T[] => {
    const aggregatedItems: { [key: string]: T } = {};

    items.forEach((item) => {
        const key = keySelector(item).toString(); // Ensure the key is a string for the dictionary

        if (aggregatedItems[key]) {
            aggregate(aggregatedItems[key], item);
        } else {
            aggregatedItems[key] = {
                ...item,
                collectionOfItems: [item],
            };
        }
    });

    if (transform) {
        Object.values(aggregatedItems).forEach(transform);
    }

    return Object.values(aggregatedItems);
};

export const getLetter = (cash: number, type?: string, invoice?: string) => {
    if (type === "DUPLICATED") {
        if (invoice === "INVOICE")
            return 'ДФ';
        else
            return 'ДС';
    }
    if ( type === "CANCELLED") {
        if (invoice === "INVOICE")
            return 'АФ';
        else
            return 'АC';
    }
    if (type === "INVOICE") {
        if (cash < 0)
            return 'КФ';
        else
            return 'Ф';
    } else if (type === "RECEIPT") {
        if (cash < 0)
            return 'КС';
        else
            return 'С';
    }
    if (type === "CREDIT_NOTE")
        return 'КФ';
    if (type === "STORNO")
        return 'КС';
}

/**
 * Добавя платената част към сумата в колоната "Обща такса": "30€ | Платено 10€".
 *
 * Показва се само при банкова фактура, по която плащането е започнало, но не е завършило. При нула и
 * при платена докрай добавката е шум - тогава клетката остава точно каквато е била. Затова се подава
 * и {@code fallback}: двете таблици смятат основната си сума различно (оборотът - от реда на
 * плащането, документите - от сбора на редовете) и това не бива да се променя оттук.
 *
 * Сумите идват в стотинки, както навсякъде в invoicing кода.
 */
export const withPaidPart = (
    fallback: string,
    total: number,
    paidAmount: number | undefined,
    currency: string | undefined,
    isBank: boolean,
    paidLabel: string
): string => {
    const paid = paidAmount ?? 0;
    if (!isBank || paid <= 0 || paid >= total) return fallback;

    const symbol = getCurrencySymbol(currency);
    const amount = (cents: number) => `${Number((cents / 100).toFixed(2))}${symbol}`;
    return `${amount(total)} | ${paidLabel} ${amount(paid)}`;
};

export const getCurrencySymbol = (currency?: string): string => {
    if (!currency) return '€';
    switch (currency.toUpperCase()) {
        case 'BGN':
            return 'лв.';
        case 'EUR':
            return '€';
        default:
            return currency;
    }
};
